// src/pages/SalesDashboard.jsx

import { useState, useEffect, useRef } from "react";
import {
  ChevronRight,
  Users,
  IndianRupee,
  CheckCircle2,
  Plus,
  Filter,
} from "lucide-react";
import TopNavbar from "../../components/AdminPortal/TopNavbar";
import { useSidebar } from "../../context/SidebarContext";
import { salesAPI } from "../../services/AdminPortal/salesAPI";
import { toast } from "react-hot-toast";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import {
  Users as UsersIcon,
  IndianRupee as IndianRupeeIcon,
  Banknote as BanknoteIcon,
} from "lucide-react";

ChartJS.register(ArcElement, Tooltip, Legend);

const StatCard = ({ title, amount, details, icon: Icon }) => (
  <div className="bg-[#131313] p-4 rounded-lg flex justify-between items-start w-full min-w-[200px]">
    <div>
      <p className="text-xs text-white mb-1">{title}</p>
      <p className="text-xl font-bold text-white mb-2">{amount}</p>
      <div className="flex flex-col gap-1">
        {details.map((detail, idx) => (
          <div key={idx} className="flex items-center justify-between w-full">
            <p className={`text-xs ${detail.color || "text-white"}`}>
              {detail.label}
            </p>
            <p
              className={`text-xs font-semibold ${
                detail.color || "text-white"
              }`}
            >
              {detail.value}
            </p>
          </div>
        ))}
      </div>
    </div>
    <div className="bg-[#FFCA20] p-2 rounded-full ml-4 flex items-center justify-center self-start">
      <Icon className="w-5 h-5 text-black" />
    </div>
  </div>
);

export default function SalesDashboard() {
  const { sidebarExpanded, isMobile } = useSidebar();
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    processingMethod: "",
    startDate: "",
    endDate: "",
    loanType: "",
    timeRange: "all",
  });
  const [newLeadSource, setNewLeadSource] = useState({
    name: "",
    leads: 0,
    processingMethod: "INTERNAL",
  });
  const [isTimeRangeDropdownOpen, setIsTimeRangeDropdownOpen] = useState(false);
  const [statsFilter, setStatsFilter] = useState("all");
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  const timeRangeOptions = [
    { value: "all", label: "All Time" },
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "year", label: "This Year" },
  ];

  const handleTimeRangeSelect = (value) => {
    setFilters((prev) => ({
      ...prev,
      timeRange: value,
    }));
    setIsTimeRangeDropdownOpen(false);
    fetchSalesData();
  };

  const fetchSalesData = async () => {
    setLoading(true);
    try {
      const res = await salesAPI.getSalesData(filters);
      if (res.success) {
        setSalesData(res.data);
      } else throw new Error(res.message);
    } catch (err) {
      setError(err.message);
      toast.error("Could not load sales data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const applyFilters = () => {
    fetchSalesData();
    setShowFilterModal(false);
  };

  const resetFilters = () => {
    setFilters({
      processingMethod: "",
      startDate: "",
      endDate: "",
      loanType: "",
      timeRange: "all",
    });
    setShowFilterModal(false);
  };

  const handleLeadSourceChange = (e) => {
    const { name, value } = e.target;
    setNewLeadSource((prev) => ({
      ...prev,
      [name]: name === "leads" ? parseInt(value) || 0 : value,
    }));
  };

  const handleAddLeadSource = async () => {
    if (!newLeadSource.name) {
      toast.error("Lead source name is required");
      return;
    }

    if (newLeadSource.leads <= 0) {
      toast.error("Number of leads must be greater than zero");
      return;
    }

    try {
      const response = await salesAPI.addLeadSource(newLeadSource);
      if (response.success) {
        toast.success("Lead source added successfully");
        setNewLeadSource({ name: "", leads: 0, processingMethod: "INTERNAL" });
        setShowModal(false);
        // Immediately fetch updated data
        fetchSalesData();
      } else {
        toast.error(response.message || "Failed to add lead source");
      }
    } catch (err) {
      console.error("Error adding lead source:", err);
      toast.error(err.message || "Failed to add lead source");
    }
  };

  // Prepare data for pie charts
  const loanTypeData =
    salesData?.loanTypesBreakdown?.map((l) => ({
      label: l.loanType,
      value: l.totalLoanAmount,
      internalAmount:
        l.processingBreakdown?.find((p) => p.processingMethod === "INTERNAL")
          ?.amount || 0,
      externalAmount:
        l.processingBreakdown?.find((p) => p.processingMethod === "EXTERNAL")
          ?.amount || 0,
    })) || [];

  const leadSourceData =
    salesData?.leadSourceBreakdown?.map((l) => ({
      label: l.source,
      value: l.leads,
      internalLeads:
        l.processingBreakdown?.find((p) => p.processingMethod === "INTERNAL")
          ?.leads || 0,
      externalLeads:
        l.processingBreakdown?.find((p) => p.processingMethod === "EXTERNAL")
          ?.leads || 0,
      approvedLeads: l.approvedLeads || 0,
      conversionRate: l.conversionRate || 0,
    })) || [];

  const insuranceData =
    salesData?.insuranceTypeBreakdown?.map((i) => ({
      label: i.insuranceType,
      value: i.totalAmount,
      internalAmount:
        i.processingBreakdown?.find((p) => p.processingMethod === "INTERNAL")
          ?.amount || 0,
      externalAmount:
        i.processingBreakdown?.find((p) => p.processingMethod === "EXTERNAL")
          ?.amount || 0,
      count: i.count || 0,
    })) || [];

  if (loading)
    return (
      <div
        className="bg-black text-white mt-12"
        style={{
          marginLeft: isMobile ? "0" : sidebarExpanded ? "200px" : "60px",
          width: isMobile
            ? "100%"
            : `calc(100% - ${sidebarExpanded ? "200px" : "60px"})`,
        }}
      >
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-yellow-500"></div>
      </div>
    );

  if (error)
    return (
      <div
        className="min-h-screen p-4 flex items-center justify-center bg-black text-red-500"
        style={{
          marginLeft: isMobile ? 0 : sidebarExpanded ? 220 : 80,
          width: isMobile
            ? "100%"
            : `calc(100% - ${sidebarExpanded ? 220 : 80}px)`,
        }}
      >
        Error: {error}
      </div>
    );

  return (
    <>
      <div
        className="min-h-screen bg-black text-white mt-12"
        style={{
          marginLeft: isMobile ? 0 : sidebarExpanded ? 220 : 80,
          width: isMobile
            ? "100%"
            : `calc(100% - ${sidebarExpanded ? 220 : 80}px)`,
        }}
      >
        <TopNavbar />
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-xl font-bold text-gray-200">Sales Dashboard</h1>
            <div className="flex gap-2">
              <select
                value={statsFilter}
                onChange={(e) => setStatsFilter(e.target.value)}
                className="bg-[#1f1f1f] text-gray-300 px-3 py-2 rounded-md text-sm font-medium border border-[#333333] focus:outline-none focus:ring-2 focus:ring-[#FFCA20]"
              >
                <option value="all">All Time</option>
                <option value="yearly">Yearly</option>
                <option value="quarterly">Quarterly</option>
                <option value="monthly">Monthly</option>
              </select>
              <button
                onClick={() => setShowFilterModal(true)}
                className="bg-[#222] text-white px-3 py-1 rounded flex items-center gap-1 hover:bg-[#333]"
              >
                <Filter size={16} /> Filter
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          {(() => {
            const multiplier = statsFilter === "monthly" ? 0.3 : statsFilter === "quarterly" ? 0.7 : statsFilter === "yearly" ? 0.9 : 1;
            const filteredData = {
              totalLoans: Math.floor((salesData?.totalLoans || 0) * multiplier),
              totalApprovedLoans: Math.floor((salesData?.totalApprovedLoans || 0) * multiplier),
              totalActiveLoans: Math.floor((salesData?.totalActiveLoans || 0) * multiplier),
              totalLoanAmount: (salesData?.totalLoanAmount || 0) * multiplier,
              revenueBreakdown: {
                internal: (salesData?.revenueBreakdown?.internal || 0) * multiplier,
                external: (salesData?.revenueBreakdown?.external || 0) * multiplier
              },
              approvedLoansBreakdown: {
                internal: Math.floor((salesData?.approvedLoansBreakdown?.internal || 0) * multiplier),
                external: Math.floor((salesData?.approvedLoansBreakdown?.external || 0) * multiplier)
              },
              platformFees: {
                total: (salesData?.platformFees?.total || 0) * multiplier,
                collected: (salesData?.platformFees?.collected || 0) * multiplier,
                pending: (salesData?.platformFees?.pending || 0) * multiplier
              }
            };

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {[
                  {
                    key: "totalLeads",
                    title: "TOTAL LEADS",
                    amount: filteredData.totalLoans?.toLocaleString("en-IN") || "0",
                    details: [
                      {
                        label: "Approved",
                        value: filteredData.totalApprovedLoans?.toLocaleString("en-IN") || "0",
                        color: "text-green-400",
                      },
                      {
                        label: "Active",
                        value: filteredData.totalActiveLoans?.toLocaleString("en-IN") || "0",
                        color: "text-yellow-400",
                      },
                    ],
                    icon: UsersIcon,
                  },
                  {
                    key: "totalLoanAmount",
                    title: "TOTAL LOAN AMOUNT",
                    amount: `₹${filteredData.totalLoanAmount?.toLocaleString("en-IN") || "0"}`,
                    details: [
                      {
                        label: "Internal",
                        value: `₹${filteredData.revenueBreakdown.internal?.toLocaleString("en-IN") || "0"}`,
                        color: "text-green-400",
                      },
                      {
                        label: "External",
                        value: `₹${filteredData.revenueBreakdown.external?.toLocaleString("en-IN") || "0"}`,
                        color: "text-yellow-400",
                      },
                    ],
                    icon: IndianRupeeIcon,
                  },
                  {
                    key: "totalApprovedLoans",
                    title: "TOTAL APPROVED LOANS",
                    amount: filteredData.totalApprovedLoans?.toLocaleString("en-IN") || "0",
                    details: [
                      {
                        label: "Internal",
                        value: filteredData.approvedLoansBreakdown.internal?.toLocaleString("en-IN") || "0",
                        color: "text-green-400",
                      },
                      {
                        label: "External",
                        value: filteredData.approvedLoansBreakdown.external?.toLocaleString("en-IN") || "0",
                        color: "text-yellow-400",
                      },
                    ],
                    icon: BanknoteIcon,
                  },
                  {
                    key: "totalRevenue",
                    title: "TOTAL PLATFORM FEE",
                    amount: `₹${filteredData.platformFees.total?.toLocaleString("en-IN") || "0"}`,
                    details: [
                      {
                        label: "Collected",
                        value: `₹${filteredData.platformFees.collected?.toLocaleString("en-IN") || "0"}`,
                        color: "text-green-400",
                      },
                      {
                        label: "Pending",
                        value: `₹${filteredData.platformFees.pending?.toLocaleString("en-IN") || "0"}`,
                        color: "text-yellow-400",
                      },
                    ],
                    icon: IndianRupeeIcon,
                  },
                ].map(({ key, title, amount, details, icon }) => (
                  <StatCard
                    key={key}
                    title={title}
                    amount={amount}
                    details={details}
                    icon={icon}
                  />
                ))}
              </div>
            );
          })()}

          {/* Loan Summary */}
          <div className="bg-[#131313] p-3 md:p-6 rounded-lg mb-8 flex flex-col lg:flex-row gap-4 md:gap-6 text-white font-sans">
            {/* Left Side - Summary + Table */}
            <div className="flex-1 flex flex-col justify-between">
              <h2 className="text-lg md:text-xl font-bold mb-3 md:mb-4">
                LOAN TYPE SUMMARY
              </h2>

              {/* Totals */}
              <div className="flex flex-col md:flex-row justify-between border-b border-[#333] pb-3 md:pb-4 mb-3 md:mb-4 text-center gap-3 md:gap-4">
                <div className="flex-1">
                  <p className="text-gray-300 text-sm">Total Loans</p>
                  <p className="text-xl md:text-2xl font-bold text-[#FFCA20]">
                    {salesData?.loanTypesBreakdown
                      ?.reduce((sum, type) => sum + type.totalLoans, 0)
                      ?.toLocaleString("en-IN") || "0"}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-gray-300 text-sm">Total Amount</p>
                  <p className="text-xl md:text-2xl font-bold text-[#FFCA20]">
                    ₹
                    {salesData?.loanTypesBreakdown
                      ?.reduce((sum, type) => sum + type.totalLoanAmount, 0)
                      ?.toLocaleString("en-IN") || "0"}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-gray-300 text-sm">Popular Loan Type</p>
                  <p className="text-xl md:text-2xl font-bold text-[#FFCA20]">
                    {salesData?.popularLoanType || "N/A"}
                  </p>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <div className="grid grid-cols-3 gap-2 md:gap-4 mb-2 text-[#BFBFBF] font-semibold text-xs md:text-sm">
                  <div>LOAN TYPE</div>
                  <div className="text-right">TOTAL LOANS</div>
                  <div className="text-right">TOTAL AMOUNT</div>
                </div>

                {salesData?.loanTypesBreakdown?.map((loanType, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-3 gap-2 md:gap-4 mb-2 text-xs md:text-sm"
                  >
                    <div className="truncate">{loanType.loanType}</div>
                    <div className="text-right">
                      {loanType.totalLoans?.toLocaleString("en-IN") || "0"}
                    </div>
                    <div className="text-right">
                      ₹{loanType.totalLoanAmount?.toLocaleString("en-IN") || "0"}
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No loan data available
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Chart */}
            <div className="flex-1 flex flex-col items-center justify-center">
              {/* Pie Chart */}
              <div className="w-full h-[180px] md:h-[220px]">
                <Pie 
                  data={{
                    labels: loanTypeData.map(item => item.label),
                    datasets: [{
                      data: loanTypeData.map(item => item.value),
                      backgroundColor: ["#8e24aa", "#5e35b1", "#e040fb", "#ff4081", "#7b1fa2", "#9c27b0"],
                      borderWidth: 0,
                      hoverOffset: 8,
                    }]
                  }}
                  options={{
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percent = ((context.raw / total) * 100).toFixed(1);
                            return `${context.label}: ₹${context.raw.toLocaleString('en-IN')} (${percent}%)`;
                          }
                        }
                      }
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                  }}
                />
              </div>

              {/* Legend */}
              <div className="w-full space-y-1 md:space-y-2 text-xs md:text-sm">
                {salesData?.loanTypesBreakdown?.map((loanType, index) => {
                  const colors = [
                    "#8e24aa",
                    "#5e35b1",
                    "#e040fb",
                    "#ff4081",
                    "#7b1fa2",
                    "#9c27b0",
                  ];
                  const totalAmount = salesData.totalLoanAmount || 1; // Avoid division by zero
                  const percentage = (
                    (loanType.totalLoanAmount / totalAmount) *
                    100
                  ).toFixed(0);

                  // Get internal/external breakdown
                  const internalAmount =
                    loanType.processingBreakdown?.find(
                      (p) => p.processingMethod === "INTERNAL"
                    )?.amount || 0;
                  const externalAmount =
                    loanType.processingBreakdown?.find(
                      (p) => p.processingMethod === "EXTERNAL"
                    )?.amount || 0;

                  return (
                    <div key={index} className="mb-2 md:mb-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span
                            className="w-3 h-3 md:w-4 md:h-4 rounded-full mr-2"
                            style={{
                              backgroundColor: colors[index % colors.length],
                            }}
                          ></span>
                          <span className="truncate">
                            {loanType.loanType} ({percentage}%)
                          </span>
                        </div>
                        <span className="font-semibold text-xs md:text-sm">
                          ₹{loanType.totalLoanAmount?.toLocaleString("en-IN") || "0"}
                        </span>
                      </div>
                      <div className="ml-5 md:ml-6 mt-1 text-xs grid grid-cols-2 gap-1 md:gap-2">
                        <div className="flex justify-between">
                          <span className="text-green-400">Internal:</span>
                          <span>
                            ₹{internalAmount?.toLocaleString("en-IN") || "0"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-yellow-400">External:</span>
                          <span>
                            ₹{externalAmount?.toLocaleString("en-IN") || "0"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }) || (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No loan data available
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <div className="bg-[#131313] p-3 md:p-6 rounded-lg mb-4 md:mb-8 text-white font-sans text-sm h-auto lg:h-[600px] flex flex-col">
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-3 md:mb-4 gap-2">
                <h2 className="text-sm md:text-base font-semibold">
                  INSURANCE TYPE SUMMARY
                </h2>
                <div className="relative w-full md:w-auto">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setIsTimeRangeDropdownOpen(!isTimeRangeDropdownOpen)
                      }
                      className="bg-transparent border-2 border-[#FFCA20] text-[#FFCA20] text-xs px-2 py-1 rounded focus:outline-none w-full md:w-auto flex justify-between items-center min-w-[100px]"
                    >
                      <span>
                        {
                          timeRangeOptions.find(
                            (opt) => opt.value === (filters.timeRange || "all")
                          )?.label
                        }
                      </span>
                      <svg
                        className={`w-3 h-3 ml-2 transition-transform ${
                          isTimeRangeDropdownOpen ? "rotate-180" : ""
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                  {isTimeRangeDropdownOpen && (
                    <div className="absolute right-0 z-10 mt-1 w-full md:w-auto min-w-[100px] bg-[#131313] border border-[#333333] rounded max-h-32 overflow-y-auto">
                      {timeRangeOptions.map((option) => (
                        <div
                          key={option.value}
                          onClick={() => handleTimeRangeSelect(option.value)}
                          className="px-2 py-1 text-xs text-white hover:bg-[#333333] cursor-pointer"
                        >
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Pie Chart */}
              <div className="flex justify-center mb-4 md:mb-6 h-[150px] md:h-[180px]">
                <Pie 
                  data={{
                    labels: insuranceData.map(item => item.label),
                    datasets: [{
                      data: insuranceData.map(item => item.value),
                      backgroundColor: ["#355E63", "#2A8C82", "#66C2BA", "#9CF6FF", "#00ACC1", "#26C6DA"],
                      borderWidth: 0,
                      hoverOffset: 8,
                    }]
                  }}
                  options={{
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percent = ((context.raw / total) * 100).toFixed(1);
                            return `${context.label}: ₹${context.raw.toLocaleString('en-IN')} (${percent}%)`;
                          }
                        }
                      }
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                  }}
                />
              </div>

              {/* Legend List */}
              <div className="space-y-2 md:space-y-3 mb-4 md:mb-6 flex-1 overflow-y-auto">
                {salesData?.insuranceTypeBreakdown?.map((insurance, index) => {
                  const colors = [
                    "#355E63",
                    "#2A8C82",
                    "#66C2BA",
                    "#9CF6FF",
                    "#00ACC1",
                    "#26C6DA",
                  ];

                  // Get internal/external breakdown
                  const internalAmount =
                    insurance.processingBreakdown?.find(
                      (p) => p.processingMethod === "INTERNAL"
                    )?.amount || 0;
                  const externalAmount =
                    insurance.processingBreakdown?.find(
                      (p) => p.processingMethod === "EXTERNAL"
                    )?.amount || 0;

                  return (
                    <div key={index} className="mb-2 md:mb-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span
                            className="w-3 h-3 rounded-full mr-2"
                            style={{
                              backgroundColor: colors[index % colors.length],
                            }}
                          ></span>
                          <span className="text-xs md:text-sm truncate">
                            {insurance.insuranceType}
                          </span>
                        </div>
                        <span className="text-xs md:text-sm font-medium">
                          ₹{insurance.totalAmount?.toLocaleString("en-IN") || "0"}
                        </span>
                      </div>
                      <div className="ml-5 md:ml-6 mt-1 text-xs grid grid-cols-2 gap-1 md:gap-2">
                        <div className="flex justify-between">
                          <span className="text-green-400">Internal:</span>
                          <span>
                            ₹{internalAmount?.toLocaleString("en-IN") || "0"}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-yellow-400">External:</span>
                          <span>
                            ₹{externalAmount?.toLocaleString("en-IN") || "0"}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                }) || (
                  <div className="text-center py-4 text-gray-400 text-sm">
                    No insurance data available
                  </div>
                )}
              </div>

              {/* Internal Label */}
              <div className="text-center text-[#CCCCCC] text-xs md:text-sm mb-3 md:mb-4">
                Internal Insurance (LONO)
              </div>

              {/* Footer Table */}
              <div className="border-t border-[#333] pt-2 md:pt-3 flex flex-col md:flex-row justify-between text-xs text-[#BFBFBF] gap-2 md:gap-0">
                <div className="text-center md:text-left">
                  <p className="mb-1">POLICIES SOLD</p>
                  <p className="text-white font-medium">
                    {salesData?.insuranceTypeBreakdown
                      ?.reduce((sum, item) => sum + (item.count || 0), 0)
                      ?.toLocaleString("en-IN") || "0"}
                  </p>
                </div>
                <div className="text-center md:text-right">
                  <p className="mb-1">TOTAL PREMIUM COLLECTED</p>
                  <p className="text-white font-medium">
                    ₹
                    {salesData?.insuranceTypeBreakdown
                      ?.reduce((sum, item) => sum + (item.totalAmount || 0), 0)
                      ?.toLocaleString("en-IN") || "0"}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#131313] p-3 md:p-6 rounded-lg mb-4 md:mb-8 text-white font-sans text-sm h-auto lg:h-[600px] flex flex-col relative">
              {/* Header */}
              <div className="flex flex-col md:flex-row justify-between items-start mb-4 md:mb-6 gap-2">
                <h2 className="text-sm md:text-base font-semibold">
                  LEAD GENERATION
                </h2>
                <button
                  onClick={() => setShowModal(true)}
                  className="bg-[#FFCA20] text-black px-3 py-1 text-xs md:text-sm rounded font-medium flex items-center gap-1 w-full md:w-auto justify-center"
                >
                  + Add Lead Source
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 text-center mb-3 md:mb-4 border-b border-[#333] pb-3 md:pb-4 text-xs">
                <div>
                  <p className="text-[#BFBFBF] mb-1">Total Leads</p>
                  <p className="text-[#FFCA20] text-base md:text-lg font-bold">
                    {salesData?.totalLoans?.toLocaleString("en-IN") || "0"}
                  </p>
                </div>
                <div>
                  <p className="text-[#BFBFBF] mb-1">Conversion Rate</p>
                  <p className="text-[#FFCA20] text-base md:text-lg font-bold">
                    {salesData?.conversionRate?.toFixed(1) || "0"}%
                  </p>
                </div>
                <div>
                  <p className="text-[#BFBFBF] mb-1">Best Lead Source</p>
                  <p className="text-[#FFCA20] text-base md:text-lg font-bold truncate">
                    {salesData?.leadSourceBreakdown?.[0]?.source || "N/A"}
                  </p>
                </div>
              </div>

              {/* Pie Chart */}
              <div className="flex justify-center mb-4 md:mb-6 h-[140px] md:h-[160px]">
                <Pie 
                  data={{
                    labels: leadSourceData.map(item => item.label),
                    datasets: [{
                      data: leadSourceData.map(item => item.value),
                      backgroundColor: ["#4A3F1E", "#9B7D1A", "#FFD600", "#F5E278", "#D4AF37", "#FFC107"],
                      borderWidth: 0,
                      hoverOffset: 8,
                    }]
                  }}
                  options={{
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function(context) {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percent = ((context.raw / total) * 100).toFixed(1);
                            return `${context.label}: ${context.raw.toLocaleString('en-IN')} (${percent}%)`;
                          }
                        }
                      }
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                  }}
                />
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-3 mb-2 text-[#BFBFBF] text-xs font-semibold">
                <div>SOURCE</div>
                <div className="text-center">LEADS</div>
                <div className="text-right">CONVERSION</div>
              </div>

              {/* Table Rows */}
              <div className="space-y-2 text-xs md:text-sm flex-1 overflow-y-auto">
                {salesData?.leadSourceBreakdown?.map((source, index) => {
                  const colors = [
                    "#4A3F1E",
                    "#9B7D1A",
                    "#FFD600",
                    "#F5E278",
                    "#D4AF37",
                    "#FFC107",
                  ];

                  // Get internal/external breakdown
                  const internalLeads =
                    source.processingBreakdown?.find(
                      (p) => p.processingMethod === "INTERNAL"
                    )?.leads || 0;
                  const externalLeads =
                    source.processingBreakdown?.find(
                      (p) => p.processingMethod === "EXTERNAL"
                    )?.leads || 0;

                  return (
                    <div key={index} className="mb-3">
                      <div className="grid grid-cols-3 items-center">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: colors[index % colors.length],
                            }}
                          ></span>
                          {source.source}
                        </div>
                        <div className="text-center">
                          {source.leads?.toLocaleString("en-IN") || "0"}
                        </div>
                        <div className="text-right">
                          {source.conversionRate?.toFixed(1) || "0"}%
                        </div>
                      </div>
                      <div className="ml-6 mt-1 text-xs grid grid-cols-3 gap-2">
                        <div className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-green-400 mr-1"></span>
                          <span>Internal: {internalLeads}</span>
                        </div>
                        <div className="flex items-center">
                          <span className="w-2 h-2 rounded-full bg-yellow-400 mr-1"></span>
                          <span>External: {externalLeads}</span>
                        </div>
                        <div className="text-right">
                          <span>Approved: {source.approvedLeads || 0}</span>
                        </div>
                      </div>
                    </div>
                  );
                }) || (
                  <div className="text-center py-4 text-gray-400">
                    No lead source data available
                  </div>
                )}
              </div>

              {/* Add Lead Source Modal */}
              {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="bg-[#1E1E1E] p-6 rounded-lg w-full max-w-sm">
                    <h3 className="text-white text-lg font-semibold mb-4">
                      Add Lead Source
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          Lead Source
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={newLeadSource.name}
                          onChange={handleLeadSourceChange}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] focus:outline-none"
                          placeholder="Enter lead source"
                        />
                        {!newLeadSource.name && (
                          <p className="text-red-500 text-xs mt-1">
                            Lead source name is required
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          No. of Leads
                        </label>
                        <input
                          type="number"
                          name="leads"
                          min="1"
                          value={newLeadSource.leads}
                          onChange={handleLeadSourceChange}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] focus:outline-none"
                          placeholder="Enter number of leads"
                        />
                        {newLeadSource.leads <= 0 && (
                          <p className="text-red-500 text-xs mt-1">
                            Number of leads must be greater than zero
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          Processing Method
                        </label>
                        <select
                          name="processingMethod"
                          value={newLeadSource.processingMethod || "INTERNAL"}
                          onChange={handleLeadSourceChange}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] focus:outline-none"
                        >
                          <option value="INTERNAL">Internal</option>
                          <option value="EXTERNAL">External</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                      <button
                        onClick={() => setShowModal(false)}
                        className="px-4 py-2 text-sm rounded bg-[#333] text-white hover:bg-[#444]"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddLeadSource}
                        disabled={
                          !newLeadSource.name || newLeadSource.leads <= 0
                        }
                        className="px-4 py-2 text-sm rounded bg-[#FFCA20] text-black font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Filter Modal */}
              {showFilterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                  <div className="bg-[#1E1E1E] p-6 rounded-lg w-full max-w-md">
                    <h3 className="text-white text-lg font-semibold mb-4">
                      Filter Sales Data
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          Processing Method
                        </label>
                        <select
                          name="processingMethod"
                          value={filters.processingMethod}
                          onChange={handleFilterChange}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] focus:outline-none"
                        >
                          <option value="">All</option>
                          <option value="INTERNAL">Internal</option>
                          <option value="EXTERNAL">External</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          Start Date
                        </label>
                        <div 
                          onClick={() => startDateRef.current?.showPicker?.() || startDateRef.current?.click()}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] cursor-pointer"
                        >
                          <input
                            ref={startDateRef}
                            type="date"
                            name="startDate"
                            value={filters.startDate}
                            onChange={handleFilterChange}
                            className="w-full bg-transparent text-white focus:outline-none cursor-pointer pointer-events-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          End Date
                        </label>
                        <div 
                          onClick={() => endDateRef.current?.showPicker?.() || endDateRef.current?.click()}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] cursor-pointer"
                        >
                          <input
                            ref={endDateRef}
                            type="date"
                            name="endDate"
                            value={filters.endDate}
                            onChange={handleFilterChange}
                            className="w-full bg-transparent text-white focus:outline-none cursor-pointer pointer-events-none"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-1 text-[#CCCCCC]">
                          Loan Type
                        </label>
                        <input
                          type="text"
                          name="loanType"
                          value={filters.loanType}
                          onChange={handleFilterChange}
                          className="w-full p-2 rounded bg-[#2A2A2A] text-white border border-[#444] focus:outline-none"
                          placeholder="Enter loan type"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-4 mt-6">
                      <button
                        onClick={resetFilters}
                        className="px-4 py-2 text-sm rounded bg-[#333] text-white hover:bg-[#444]"
                      >
                        Reset
                      </button>
                      <button
                        onClick={applyFilters}
                        className="px-4 py-2 text-sm rounded bg-[#FFCA20] text-black font-semibold"
                      >
                        Apply Filters
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
